import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, CheckCircle2, XCircle, AlertTriangle, Loader2, Keyboard, Camera } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function GatePage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState("");
  const [looking, setLooking] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [stats, setStats] = useState(null);
  const [role, setRole] = useState("");
  const scannerRef = useRef(null);
  const busyRef = useRef(false);

  const loadStats = useCallback(() => {
    api.get("/admin/gate/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setRole(data.role || ""); setAuthed(true); }).catch(() => navigate("/admin"));
  }, [navigate]);

  useEffect(() => {
    if (authed) loadStats();
  }, [authed, loadStats]);

  const lookup = useCallback(async (code) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setLooking(true);
    setError("");
    try {
      const { data } = await api.get("/admin/tickets/lookup", { params: { code } });
      setTicket(data);
    } catch (err) {
      setTicket(null);
      setError(apiError(err, "Ticket not found"));
    }
    setLooking(false);
    setTimeout(() => { busyRef.current = false; }, 1500);
  }, []);

  useEffect(() => {
    if (!authed) return;
    const scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;
    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 240, height: 240 } },
      (text) => lookup(text.trim()),
      () => {}
    ).then(() => setScanning(true)).catch(() => setCameraError("Camera unavailable — use manual entry below."));
    return () => {
      scanner.stop().catch(() => {});
    };
  }, [authed, lookup]);

  const checkIn = async () => {
    setCheckingIn(true);
    try {
      await api.patch(`/admin/bookings/${ticket.id}`, { action: "checkin" });
      setTicket({ ...ticket, checked_in: true });
      loadStats();
      toast.success(`${ticket.name} checked in — ${ticket.admits || ticket.quantity} guests`);
    } catch (err) {
      toast.error(apiError(err, "Check-in failed"));
    }
    setCheckingIn(false);
  };

  const reset = () => { setTicket(null); setError(""); setManual(""); };

  const statusOf = (t) => {
    if (t.status === "cancelled") return { label: "CANCELLED — do not admit", cls: "border-red-400/60 text-red-400", icon: XCircle };
    if (t.status !== "confirmed") return { label: "UNPAID — do not admit", cls: "border-red-400/60 text-red-400", icon: XCircle };
    if (t.checked_in) return { label: "ALREADY CHECKED IN", cls: "border-gold/60 text-gold", icon: AlertTriangle };
    return { label: "VALID — admit", cls: "border-green-400/60 text-green-400", icon: CheckCircle2 };
  };

  if (!authed) return <div className="min-h-screen bg-ink text-ash flex items-center justify-center">Checking session…</div>;

  const s = ticket ? statusOf(ticket) : null;

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="gate-page">
      <header className="px-6 h-16 flex items-center justify-between border-b border-bone/10 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <div className="flex items-baseline gap-2">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-xs tracking-[0.25em] text-bone">GATE</span>
        </div>
        {role === "admin" ? (
          <Link to="/admin/dashboard" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="gate-admin-link">Admin</Link>
        ) : (
          <button onClick={() => { localStorage.removeItem("sumiran_token"); navigate("/admin"); }}
            className="text-xs text-ash hover:text-red-400 transition-colors" data-testid="gate-logout-btn">Logout</button>
        )}
      </header>

      <main className="px-6 py-10 max-w-lg mx-auto">
        {stats && (
          <div className="mb-8 border border-bone/15 bg-surface p-5" data-testid="gate-stats">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[9px] tracking-[0.25em] text-ash uppercase">Checked in</p>
                <p className="font-display text-3xl text-gold leading-tight" data-testid="gate-stats-checked">
                  {stats.checked_in_guests}<span className="text-bone/50 text-xl"> / {stats.total_guests}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-[9px] tracking-[0.25em] text-ash uppercase">Remaining</p>
                <p className="font-display text-3xl text-bone leading-tight" data-testid="gate-stats-remaining">{stats.remaining_guests}</p>
              </div>
            </div>
            <div className="mt-3 h-1.5 bg-ink overflow-hidden">
              <div className="h-full bg-saffron transition-[width] duration-500"
                style={{ width: stats.total_guests ? `${(stats.checked_in_guests / stats.total_guests) * 100}%` : "0%" }}
                data-testid="gate-stats-bar" />
            </div>
            <p className="mt-2.5 text-[10px] text-ash">{stats.checked_in_bookings} of {stats.total_bookings} tickets checked in</p>
          </div>
        )}

        <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase flex items-center gap-2">
          <ScanLine size={15} /> Scan ticket QR
        </p>

        <div className="mt-5 border border-bone/15 bg-surface overflow-hidden" data-testid="qr-reader-box">
          <div id="qr-reader" className="w-full" />
          {cameraError && <p className="p-5 text-sm text-ash flex items-center gap-2"><Camera size={15} /> {cameraError}</p>}
        </div>

        <div className="mt-5 flex gap-2">
          <div className="relative flex-1">
            <Keyboard size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ash" />
            <input value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} placeholder="Or type ticket code (SUM-…)"
              className="w-full bg-ink border border-bone/15 pl-10 pr-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors tracking-widest"
              data-testid="gate-manual-input" />
          </div>
          <button onClick={() => manual.trim() && lookup(manual)} disabled={looking}
            className="px-6 py-3.5 bg-saffron text-ink text-sm font-medium hover:bg-gold transition-colors disabled:opacity-50"
            data-testid="gate-lookup-btn">
            {looking ? <Loader2 size={16} className="animate-spin" /> : "Verify"}
          </button>
        </div>

        {error && (
          <div className="mt-6 border border-red-400/60 p-6 text-center" data-testid="gate-error">
            <XCircle size={36} className="mx-auto text-red-400" />
            <p className="mt-3 text-red-400 font-medium">{error}</p>
            <button onClick={reset} className="mt-4 text-xs text-ash hover:text-bone transition-colors" data-testid="gate-error-reset">Scan next</button>
          </div>
        )}

        {ticket && s && (
          <div className={`mt-6 border p-7 ${s.cls}`} data-testid="gate-result">
            <div className="flex items-center gap-3">
              <s.icon size={30} />
              <p className="font-display text-sm tracking-[0.2em]">{s.label}</p>
            </div>
            <div className="mt-5 space-y-1.5 text-sm">
              <p className="font-serif text-3xl text-bone" data-testid="gate-ticket-code">{ticket.ticket_code}</p>
              <p className="text-bone/80" data-testid="gate-guest-name">{ticket.name}</p>
              <p className="text-ash">{ticket.event_name}</p>
              <p className="text-ash">{ticket.event_date} · {ticket.event_time}</p>
              <p className="text-saffron" data-testid="gate-headcount">{ticket.tier} · {ticket.admits || ticket.quantity} guest{(ticket.admits || ticket.quantity) > 1 ? "s" : ""}</p>
            </div>
            <div className="mt-6 flex gap-3">
              {ticket.status === "confirmed" && !ticket.checked_in && (
                <button onClick={checkIn} disabled={checkingIn}
                  className="flex-1 py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="gate-checkin-btn">
                  {checkingIn ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                  {checkingIn ? "Checking in…" : "Check In"}
                </button>
              )}
              <button onClick={reset} className="flex-1 py-4 border border-bone/25 text-bone hover:border-saffron hover:text-saffron transition-colors" data-testid="gate-next-btn">
                Scan Next
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
